import { BoundaryItem } from "./useSimulationModelsBoundaries";
import { Box, BoxProps, Theme, Typography, useTheme } from "@mui/material";
import { ComponentProps, useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { formatNumber } from "@app/utils";
import {
  ChartTooltip,
  RenderTooltip,
  useChartTooltip,
} from "@app/components/ChartTooltip";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { ChartDataset, ChartTypeRegistry } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { CarChartLegend } from "@app/components/ChartLegend";

type Options = ComponentProps<typeof Chart>["options"];

interface ModelsBoundariesChartDataPoint {
  x: number;
  y: number;
  boundaryType: string;
}

interface ModelsBoundariesChartData {
  xLabel: string;
  yLabel: string;
  datasets: ChartDataset<"line", ModelsBoundariesChartDataPoint[]>[];
}

export const getModelsBoundariesChartData = ({
  theme,
  items,
  forPdf,
}: {
  theme: Theme;
  items: BoundaryItem[];
  forPdf?: boolean;
}): ModelsBoundariesChartData => {
  const upperBoundaryColor = theme.palette.chartsColor.orange;
  const lowerBoundaryColor = theme.palette.chartsColor.green;

  const result: ModelsBoundariesChartData = {
    datasets: [
      {
        type: "line",
        id: "upper_boundary",
        label: "Upper Boundary",
        data: items.map((i) => ({
          x: i.year,
          y: i.upperBoundaryRate ?? 0,
          boundaryType: i.boundaryType,
        })),
        borderColor: upperBoundaryColor,
        pointBorderColor: upperBoundaryColor,
        pointBackgroundColor: upperBoundaryColor,
        pointBorderWidth: 1,
        pointRadius: forPdf ? 1 : 3,
        borderWidth: 1,
      },
      {
        type: "line",
        id: "lower_boundary",
        label: "Lower Boundary",
        data: items.map((i) => ({
          x: i.year,
          y: i.lowerBoundaryRate ?? 0,
          boundaryType: i.boundaryType,
        })),
        borderColor: lowerBoundaryColor,
        pointBorderColor: lowerBoundaryColor,
        pointBackgroundColor: lowerBoundaryColor,
        pointBorderWidth: 1,
        pointRadius: forPdf ? 1 : 3,
        borderWidth: 1,
        hoverBorderColor: "black",
        hoverBackgroundColor: "black",
      },
    ],
    xLabel: "Boundary Year",
    yLabel: "Real Return",
  };

  return result;
};

interface SimulationModelsBoundariesChartProps {
  sx?: BoxProps["sx"];
  items: BoundaryItem[];
  isWhiteContext?: boolean;
}

export const SimulationModelsBoundariesBoundaryChart = ({
  sx,
  items,
  isWhiteContext,
}: SimulationModelsBoundariesChartProps) => {
  const theme = useTheme();

  const { datasets, yLabel, xLabel } = useMemo(
    () =>
      getModelsBoundariesChartData({
        theme,
        items,
      }),
    [theme, items],
  );

  const { tooltipPlugin, tooltipData } = useChartTooltip({
    chartName: "SimulationModelsBoundariesChart",
  });

  const formatXValue = (value: number | string) =>
    typeof value === "number" ? formatNumber(value, { decimalPlaces: 2 }) : "";

  const formatYValue = (value: number | string) =>
    typeof value === "number" ? formatNumber(value, { decimalPlaces: 2 }) : "";

  const chartRef = useRef<ChartJSOrUndefined<
    keyof ChartTypeRegistry,
    ModelsBoundariesChartDataPoint[],
    unknown
  > | null>();

  const renderTooltip: RenderTooltip = (datasetIndex, dataIndex) => {
    const ds = datasets[datasetIndex];

    const xValue = `${xLabel}: ${formatXValue(ds?.data?.[dataIndex]?.x)} (${ds?.data?.[dataIndex]?.boundaryType})`;
    const yValue = `${yLabel}: ${formatYValue(ds?.data?.[dataIndex]?.y)}`;

    return (
      <>
        <Box
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: "black",
            maxWidth: 300,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="par01SemiBold" sx={{ fontSize: 15 }}>
            {ds?.label}
          </Typography>
          <Typography variant="par01Regular" sx={{ mt: 1, fontSize: 13 }}>
            {xValue}
          </Typography>
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {yValue}
          </Typography>
        </Box>
      </>
    );
  };

  const options = useMemo(() => {
    const result: Options = {
      maintainAspectRatio: false,
      animation: {
        duration: 500,
      },
      scales: {
        x: {
          offset: true,
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: formatXValue,
          },
          type: "linear",
          title: {
            text: xLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 16,
              weight: 600,
            },
          },
          grid: ChartUtils.getGridOptions({
            theme,
            showMinMaxLines: true,
          }),
        },
        y: {
          offset: true,
          type: "linear",
          title: {
            text: yLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 16,
              weight: 600,
            },
          },
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: formatYValue,
          },
          grid: ChartUtils.getGridOptions({ theme, showMinMaxLines: true }),
        },
      },
      interaction: {
        mode: "point",
        intersect: false,
      },
      plugins: {
        roundedBackground: ChartUtils.getRoundedBackgroundOptions({
          theme,
          isWhiteContext,
        }),
        tooltip: tooltipPlugin,
      },
    };
    return result;
  }, [theme, xLabel, yLabel, isWhiteContext, tooltipPlugin]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <ChartTooltip
        tooltipData={tooltipData}
        renderTooltip={renderTooltip}
        shiftY={0}
        sx={{
          /* Some bug: can't specify height on chart because it starts to grow, so put chart in the box */
          height: 480,
          position: "relative",
        }}
      >
        <Chart
          ref={chartRef}
          type="line"
          datasetIdKey="id"
          data={{ datasets }}
          options={options}
          plugins={[
            ChartPlugins.roundedBackground,
            ChartPlugins.rangeHighlight,
          ]}
        />
      </ChartTooltip>
      <CarChartLegend
        sx={{
          mt: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
        labelVariant="caption"
        items={datasets.map((ds) => ({
          label: ds.label ?? "",
          color: String(ds.borderColor),
        }))}
      />
    </Box>
  );
};
